import {
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { NgClass } from '@angular/common';
import { DashboardTranslateKeys } from '../../../utils/dashboard-translate-keys.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { FiltersComponent } from '../../filters/filters.component';
import { SkeletonDashCardComponent } from '../../skeleton/skeleton.component';
import { FilterDashOption } from '../../../utils/filters-dash.enum';
import { Company, ContractType, TranslateKey } from '@job-v2/ats-utils';
import { AppGlobalService } from 'apps/job-ats/src/app/commons/services/app-global.service';
import { EMPTY, Subject, switchMap, takeUntil } from 'rxjs';
import { DashboardService } from '../../../services/dashboard.service';
import { MessageService } from 'primeng/api';
import {
  MonthlyRecruitmentCarriedOut,
  StatisticsCriteria,
} from '../../../utils/dashboard.data';
import { NumberSpacePipe } from '../../../utils/number-space.pipe';
import { BarChartComponent } from '../../../charts/bar-chart/bar-chart.component';
import { StructuresService } from 'apps/job-ats/src/app/structures/services/structures.service';

@Component({
  imports: [
    ButtonModule,
    NgClass,
    TranslateModule,
    DatePickerModule,
    SelectModule,
    FormsModule,
    NumberSpacePipe,
    FiltersComponent,
    BarChartComponent,
    SkeletonDashCardComponent,
  ],
  selector: 'app-recruitment-perf',
  templateUrl: './recruitment-perf.component.html',
  styleUrl: './recruitment-perf.component.scss',
})
export class RecruitmentPerfComponent implements OnInit, OnDestroy {
  readonly selectedJobOwners = input<string[]>([]);
  private readonly selectedJobOwners$ = toObservable(this.selectedJobOwners);
  public isCollapsed = signal(false);
  public isLoadingRecrChart = signal(false);
  private readonly translate = inject(TranslateService);
  private readonly message = inject(MessageService);
  private readonly service = inject(DashboardService);
  private readonly structuresService = inject(StructuresService);
  private company!: Company;
  readonly appGlobalService = inject(AppGlobalService);
  private readonly destroy$ = new Subject<void>();
  private readonly hydrateOffersSubject$ = new Subject<void>();
  public DashboardTranslateKeys = DashboardTranslateKeys;
  public appReceivedFirstFilters: FilterDashOption[] = [];
  public appReceivedSecondFilters: FilterDashOption[] = [];
  public appRecrutedFilters: FilterDashOption[] = [];
  public appPostToFillFilters: FilterDashOption[] = [];
  public isLoadingReceivedApplications = signal(false);
  public isLoadingAcceptedApplications = signal(false);
  public isLoadingPositionsToFill = signal(false);
  public receivedApplicationsNumber = signal(0);
  public acceptedApplicationsNumber = signal(0);
  public positionsToFillNumber = signal(0);
  public recruitmentsCarriedOut = signal<MonthlyRecruitmentCarriedOut[]>([]);
  private receivedApplicationCriteria = new StatisticsCriteria();
  private acceptedApplicationCriteria = new StatisticsCriteria();
  private positionsToFillCriteria = new StatisticsCriteria();
  private recrutmentCarriedOutCriteria = new StatisticsCriteria();

  ngOnInit(): void {
    this.initializeDefaultDateFilters();
    this.initializeFilters();
    this.setupHydrateOffersStream();
    this.selectedJobOwners$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedJobOwners) => {
        this.handleSelectedJobOwnersChange(selectedJobOwners);
      });
    this.appGlobalService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user?.company) {
          this.company = user.company;
          this.receivedApplicationCriteria.companyId = this.company._id;
          this.acceptedApplicationCriteria.companyId = this.company._id;
          this.positionsToFillCriteria.companyId = this.company._id;
          this.recrutmentCarriedOutCriteria.companyId = this.company._id;
          this.hydrateStructures();
          this.handleSelectedJobOwnersChange(this.selectedJobOwners());
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeDefaultDateFilters(): void {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear().toString();
    const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const currentMonthYear = `${currentMonth}-${currentYear}`;

    // Set default year for received applications
    this.receivedApplicationCriteria.year = currentYear;

    // Set default month-year for accepted applications and positions to fill
    this.acceptedApplicationCriteria.monthYear = currentMonthYear;
    this.positionsToFillCriteria.monthYear = currentMonthYear;

    // Set default year for recruitment carried out
    this.recrutmentCarriedOutCriteria.year = currentYear;
  }

  hydrateStructures() {
    if (!this.company?._id) return;
    this.structuresService
      .getCompanyStructures(this.company._id as string)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const filterOptions = res.map((elt) => ({
            label: elt.name as string,
            value: elt._id as string,
          }));
          this.appRecrutedFilters = [
            ...this.appRecrutedFilters,
            ...filterOptions,
          ];
        },
      });
  }

  private setupHydrateOffersStream(): void {
    this.hydrateOffersSubject$
      .pipe(
        switchMap(() => {
          if (!this.company?._id) return EMPTY;
          return this.service.getLatestOffers(
            this.company._id as string,
            this.selectedJobOwners(),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((res) => {
        const options = res.map((elt) => ({
          label: elt.title as string,
          value: elt.id as string,
        }));
        this.appReceivedFirstFilters = [
          ...this.appReceivedFirstFilters,
          ...options,
        ];
        this.appPostToFillFilters = [...this.appPostToFillFilters, ...options];
      });
  }

  hydrateOffers(): void {
    this.hydrateOffersSubject$.next();
  }

  loadReceivedApplications() {
    if (!this.company) return;
    this.isLoadingReceivedApplications.set(true);
    this.service
      .getTotalApplications(this.receivedApplicationCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.receivedApplicationsNumber.set(res);
          this.isLoadingReceivedApplications.set(false);
        },
        error: () => {
          this.displayErrorMessage();
          this.isLoadingReceivedApplications.set(false);
        },
      });
  }

  loadAcceptedApplications() {
    if (!this.company) return;
    this.isLoadingAcceptedApplications.set(true);
    this.service
      .getTotalAcceptedApps(this.acceptedApplicationCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.acceptedApplicationsNumber.set(res);
          this.isLoadingAcceptedApplications.set(false);
        },
        error: () => {
          this.displayErrorMessage();
          this.isLoadingAcceptedApplications.set(false);
        },
      });
  }

  loadPositionsToFill() {
    if (!this.company) return;
    this.isLoadingPositionsToFill.set(true);
    this.service
      .getTotalPositionsToFill(this.positionsToFillCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.positionsToFillNumber.set(res);
          this.isLoadingPositionsToFill.set(false);
        },
        error: () => {
          this.displayErrorMessage();
          this.isLoadingPositionsToFill.set(false);
        },
      });
  }

  loadRecruitmentCarriedOut() {
    if (!this.company) return;
    this.isLoadingRecrChart.set(true);
    this.service
      .getMonthlyRecruitmentsCarriedOut(this.recrutmentCarriedOutCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.recruitmentsCarriedOut.set(res || []);
          this.isLoadingRecrChart.set(false);
        },
        error: () => {
          this.displayErrorMessage();
          this.isLoadingRecrChart.set(false);
        },
      });
  }

  private displayErrorMessage() {
    const summary = this.translate.instant(DashboardTranslateKeys.ERROR);
    const detail = this.translate.instant(
      DashboardTranslateKeys.ERROR_TRY_AGAIN,
    );
    this.message.add({ severity: 'error', summary, detail });
  }

  private updateCriteria<T extends { monthYear?: string; year?: string }>(
    criteria: T,
    key: keyof T,
    value: string | null,
  ): boolean {
    const currentValue = criteria[key];
    const hasChanged = currentValue !== value;
    
    if (value) {
      criteria[key] = value as T[keyof T];
    } else {
      delete criteria[key];
    }
    
    return hasChanged;
  }

  private handleSelectedJobOwnersChange(selectedJobOwners: string[]) {
    if (!this.company?._id) return;
    this.updateSelectedJobOwners(selectedJobOwners);
    this.loadReceivedApplications();
    this.loadAcceptedApplications();
    this.loadPositionsToFill();
    this.loadRecruitmentCarriedOut();
    this.resetOfferFilters();
    this.hydrateOffers();
  }

  private resetOfferFilters() {
    this.appReceivedFirstFilters = this.appReceivedFirstFilters.filter(
      (item) => item.value === null,
    );
    this.appPostToFillFilters = this.appPostToFillFilters.filter(
      (item) => item.value === null,
    );
  }

  private updateSelectedJobOwners(selectedJobOwners: string[]) {
    this.receivedApplicationCriteria.selectedJobOwner = selectedJobOwners;
    this.acceptedApplicationCriteria.selectedJobOwner = selectedJobOwners;
    this.positionsToFillCriteria.selectedJobOwner = selectedJobOwners;
    this.recrutmentCarriedOutCriteria.selectedJobOwner = selectedJobOwners;
  }

  updateJobsCount($event: string | null) {
    const hasChanged = this.updateCriteria(this.receivedApplicationCriteria, 'offer', $event);
    if (hasChanged) {
      this.loadReceivedApplications();
    }
  }

  updateReceivedAppsContractType($event: string | null) {
    const hasChanged = this.updateCriteria(
      this.receivedApplicationCriteria,
      'contractType',
      $event,
    );
    if (hasChanged) {
      this.loadReceivedApplications();
    }
  }

  updateReceivedAppsYear($event: string | null) {
    const hasChanged = this.updateCriteria(this.receivedApplicationCriteria, 'year', $event);
    if (hasChanged) {
      this.loadReceivedApplications();
    }
  }

  updateAcceptedAppsDepartement($event: string | null) {
    const hasChanged = this.updateCriteria(this.acceptedApplicationCriteria, 'department', $event);
    if (hasChanged) {
      this.loadAcceptedApplications();
    }
  }

  updatePositionsToFillOffer($event: string | null) {
    const hasChanged = this.updateCriteria(this.positionsToFillCriteria, 'offer', $event);
    if (hasChanged) {
      this.loadPositionsToFill();
    }
  }

  updateAcceptedAppsMonthYear($event: string | null) {
    const hasChanged = this.updateCriteria(this.acceptedApplicationCriteria, 'monthYear', $event);
    if (hasChanged) {
      this.loadAcceptedApplications();
    }
  }

  updatePositionsToFillMonthYear($event: string | null) {
    const hasChanged = this.updateCriteria(this.positionsToFillCriteria, 'monthYear', $event);
    if (hasChanged) {
      this.loadPositionsToFill();
    }
  }

  updateRecrCarriedOutYear($event: string | null) {
    const hasChanged = this.updateCriteria(this.recrutmentCarriedOutCriteria, 'year', $event);
    if (hasChanged) {
      this.loadRecruitmentCarriedOut();
    }
  }

  initializeFilters() {
    const translations = {
      offer: this.translate.instant(DashboardTranslateKeys.OFFERS),
      contract: this.translate.instant(DashboardTranslateKeys.CONTRACT),
      structure: this.translate.instant(DashboardTranslateKeys.STRUCTURE),
      consulting: this.translate.instant(TranslateKey.CONTRACT_TYPE_CONSULTING),
      interim: this.translate.instant(TranslateKey.CONTRACT_TYPE_INTERIM),
      internship: this.translate.instant(TranslateKey.CONTRACT_TYPE_INTERNSHIP),
      partTime: this.translate.instant(TranslateKey.CONTRACT_TYPE_PART_TIME),
      others: this.translate.instant(TranslateKey.CONTRACT_TYPE_OTHER),
    };
    this.appReceivedFirstFilters = [
      { label: translations.offer, value: null }
    ];
    this.appReceivedSecondFilters = [
      { label: translations.contract, value: null },
      { label: ContractType.CDD, value: ContractType.CDD },
      { label: ContractType.CDI, value: ContractType.CDI },
      { label: translations.interim, value: ContractType.INTERIM },
      { label: translations.internship, value: ContractType.INTERNSHIP },
      { label: translations.partTime, value: ContractType.PART_TIME },
      { label: translations.consulting, value: ContractType.CONSULTING },
      { label: translations.others, value: ContractType.OTHERS },
    ];
    this.appRecrutedFilters = [{ label: translations.structure, value: null }];
    this.appPostToFillFilters = [{ label: translations.offer, value: null }];
  }

  toggleContent() {
    this.isCollapsed.set(!this.isCollapsed());
  }
}
